import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { Alert, Col, Modal } from "components/bootstrap";
import { Select, Spinner } from "components/common";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import { getCredentialsDefinition } from "data-tiering/repository-credentials";
import FileSystemRepositoryModalForm from "./FileSystemRepositoryModalForm";
import CloudStorageRepositoryModalForm from "./CloudStorageRepositoryModalForm";
import HadoopStorageRepositoryModalForm from "./HadoopStorageRepositoryModalForm";
import RepositoryAccessInfo from "./RepositoryAccessInfo";
import { STORAGE_REPOSITORY_TYPE, S3_COMPATIBLE_REPOSITORY_TYPE, STORAGE_REPOSITORY_TYPE_LABELS } from "./types";
import buildRepositoryOptions from "./logic/repositoryOptions";
import useDataTiering from "../hooks/useDataTiering";
const RepositorySelectContainer = styled(Col)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    font-size: ${theme.fonts.size.large};
    gap: ${theme.spacings.md};
    margin-bottom: ${theme.spacings.md};
    padding-left: ${theme.spacings.lg};
    padding-right: ${theme.spacings.lg};
  `
);
const ConfiguredBackendsAlert = styled(Alert)(
  ({ theme }) => css`
    margin: 0 ${theme.spacings.md} ${theme.spacings.md};
  `
);
const CredentialInfoBoxes = styled.div(
  ({ theme }) => css`
    margin: 0 ${theme.spacings.md};
  `
);
const SelectLabel = styled.label`
  font-weight: bold;
  font-size: ${({ theme }) => theme.fonts.size.body};
  margin-bottom: ${({ theme }) => theme.spacings.xs};
`;
const CreateRepositoryModal = ({ hideModal, show, type: forcedType = void 0 }) => {
  var _a, _b, _c;
  const { t } = useTranslation("enterprise_data_tiering");
  const { readiness } = useDataTiering();
  const [selectedRepoLocation, setSelectedRepoLocation] = useState(void 0);
  const repositoryOptions = buildRepositoryOptions((_b = (_a = readiness.data) == null ? void 0 : _a.details.supported_repository_types) != null ? _b : []);
  const soleRepoLocation = repositoryOptions.length === 1 ? repositoryOptions[0].value : void 0;
  const effectiveRepoLocation = (_c = forcedType != null ? forcedType : selectedRepoLocation) != null ? _c : soleRepoLocation;
  const credentialsDefinition = effectiveRepoLocation ? getCredentialsDefinition(effectiveRepoLocation) : void 0;
  const SetupInfo = credentialsDefinition == null ? void 0 : credentialsDefinition.create.SetupInfo;
  const handleRepositoryTypeChange = (selectedOption) => {
    setSelectedRepoLocation(selectedOption);
  };
  const repositoryForm = () => {
    var _a2;
    if (readiness.isLoading) return /* @__PURE__ */ React.createElement(Spinner, { text: t("create_repository.fetching_configuration") });
    const docsLinkComponent = /* @__PURE__ */ React.createElement(
      DocumentationLink,
      {
        page: DocsHelper.PAGES.DATA_TIERING_WARM_TIER_SETUP,
        text: t("create_repository.documentation"),
        displayIcon: true
      }
    );
    if (((_a2 = readiness.data) == null ? void 0 : _a2.details.supported_repository_types.length) === 0) {
      return /* @__PURE__ */ React.createElement(ConfiguredBackendsAlert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "create_repository.no_backend", components: { docsLink: docsLinkComponent } }));
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ConfiguredBackendsAlert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "create_repository.backend_info", components: { docsLink: docsLinkComponent } })), credentialsDefinition && /* @__PURE__ */ React.createElement(CredentialInfoBoxes, null, SetupInfo && /* @__PURE__ */ React.createElement(SetupInfo, null), /* @__PURE__ */ React.createElement(RepositoryAccessInfo, null)), !forcedType && repositoryOptions.length > 1 && /* @__PURE__ */ React.createElement(RepositorySelectContainer, null, /* @__PURE__ */ React.createElement(SelectLabel, { htmlFor: "repository-type-select" }, t("create_repository.location_label")), /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "repository-type-select",
        "aria-label": t("create_repository.location_aria"),
        placeholder: t("create_repository.location_placeholder"),
        value: effectiveRepoLocation,
        onChange: handleRepositoryTypeChange,
        options: repositoryOptions,
        menuPlacement: "auto"
      }
    )), (effectiveRepoLocation === STORAGE_REPOSITORY_TYPE.S3 || effectiveRepoLocation === STORAGE_REPOSITORY_TYPE.GCS || effectiveRepoLocation === STORAGE_REPOSITORY_TYPE.ABS || effectiveRepoLocation === S3_COMPATIBLE_REPOSITORY_TYPE.OVH || effectiveRepoLocation === S3_COMPATIBLE_REPOSITORY_TYPE.SYSELEVEN) && /* @__PURE__ */ React.createElement(
      CloudStorageRepositoryModalForm,
      {
        key: effectiveRepoLocation,
        hideModal,
        type: effectiveRepoLocation
      }
    ), effectiveRepoLocation === STORAGE_REPOSITORY_TYPE.HDFS && /* @__PURE__ */ React.createElement(HadoopStorageRepositoryModalForm, { hideModal }), effectiveRepoLocation === STORAGE_REPOSITORY_TYPE.FS && /* @__PURE__ */ React.createElement(FileSystemRepositoryModalForm, { hideModal }));
  };
  return /* @__PURE__ */ React.createElement(Modal, { show, bsSize: "large", onHide: hideModal }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("create_repository.title", {
    type: effectiveRepoLocation ? `${STORAGE_REPOSITORY_TYPE_LABELS[effectiveRepoLocation]} ` : ""
  }))), repositoryForm());
};
export default CreateRepositoryModal;
